import { type ComponentProps, useMemo } from 'react';

import {
  FormatType,
  getOtherColor,
  getPrimaryColor,
  LineChart,
  type LineData,
} from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import type { TimeseriesDataSet } from '../DataProvider/types';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import {
  isPostingMetric,
  type MetricValues,
} from '../PageConfigProvider/types/metric';
import { useChart } from './ChartProvider';
import { deriveSharedChartType } from './chart-menu/plot-share/deriveSharedChartType';
import { usePlotShareData } from './chart-menu/plot-share/usePlotShare';
import {
  isParameterSelectionEqualToDataPoint,
  type ParameterSelection,
} from './utils/check-datapoint-in-parameter';
import type { AxisConfig } from './utils/compute-axis-bounds';
import { getFormat } from './utils/getFormat';

const HIGHLIGHT_LINE_STROKE_WIDTH = 3;

type LineChartComponentProps = ComponentProps<typeof LineChart>;

const getDateFormatForMetric = (metric?: MetricValues) => {
  if (isPostingMetric(metric)) {
    return 'day';
  }

  return 'month';
};

export const Line = ({
  data,
  metricOverride,
  displayAs: displayAsOverride,
  primaryParameterValues,
  yAxis,
}: {
  data: TimeseriesDataSet['data'];
  metricOverride?: MetricValues;
  selectedSegments?: Array<{ id: string; name: string }>;
  displayAs?: 'value' | 'share' | 'auto';
  primaryParameterValues?: ParameterSelection[];
  yAxis?: AxisConfig;
}) => {
  const { item, variant } = useChart();
  const metricFromPlot = usePlotMetric();
  const metric = metricOverride ?? metricFromPlot;
  const { code } = useCurrencyPreference();

  const normalizedDisplayAs = getDisplayAs({ item, metric });
  const displayAs = displayAsOverride ?? normalizedDisplayAs;

  const lineData: LineData[] = useMemo(() => {
    return data.map((d) => {
      return {
        label: d.dim,
        values: d.timeseries.map((t): LineData['values'][number] => {
          if (displayAs === 'share') {
            return {
              date: t.date,
              value: t.share ?? null,
              ...(typeof t.value === 'number'
                ? { secondaryValue: t.value }
                : {}),
            };
          }

          return {
            date: t.date,
            value: t.value,
            ...(typeof t.secondaryValue === 'number'
              ? { secondaryValue: t.secondaryValue }
              : {}),
          };
        }),
        ...(primaryParameterValues?.length && {
          color: primaryParameterValues?.some((p) =>
            isParameterSelectionEqualToDataPoint(p, d)
          )
            ? getPrimaryColor()
            : getOtherColor(),
        }),
        ...(variant === 'report' &&
          primaryParameterValues?.some((p) =>
            isParameterSelectionEqualToDataPoint(p, d)
          ) && {
            strokeWidth: HIGHLIGHT_LINE_STROKE_WIDTH,
          }),
      };
    });
  }, [data, displayAs, primaryParameterValues, variant]);

  const format =
    displayAs === 'share'
      ? FormatType.PERCENTAGE
      : (item?.plot?.format ?? getFormat({ metric, displayAs: 'value' }));
  const dateFormat = getDateFormatForMetric(metric);

  const showDiffLabel = item?.plot?.showDiffLabel ?? false;
  const showLegend = item?.plot?.showLegend ?? false;
  const curveSmoothness = item?.plot?.curveSmoothness ?? 0;
  const curveTension = 1 - curveSmoothness;

  const chartProps: LineChartComponentProps = {
    data: lineData,
    format,
    dateFormat,
    currencyCode: code,
    showGridLines: true,
    colors: item?.plot?.colorPalette,
    showDiffLabel,
    showLegend,
    curveTension,
    fontSize: item?.plot?.fontSize ?? 11,
    tooltipFormat: format,
    ...(yAxis && { yAxis }),
  };

  usePlotShareData({
    data: lineData,
    chartType: deriveSharedChartType(LineChart),
    chartProps,
  });

  return <LineChart {...chartProps} />;
};

const getDisplayAs = ({
  item,
  metric,
}: {
  item: PositionedItem | undefined;
  metric?: MetricValues;
}): 'value' | 'share' | 'auto' => {
  if (!item?.plot || metric !== 'headcount') return 'auto';

  const isDisplayModeEnabled =
    item.plot.chartType === 'line' &&
    metric === 'headcount' &&
    !!item.plot.secondaryDimension &&
    item.plot.secondaryDimension !== 'timeseries';

  return isDisplayModeEnabled ? 'share' : 'auto';
};
